
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    IconButton,
    CircularProgress,
    Avatar,
    Divider,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Button,
    TextField,
    InputAdornment,
    useTheme,
    alpha,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Skeleton
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Phone as PhoneIcon,
    CalendarMonth as CalendarMonthIcon,
    People as PeopleIcon,
    Search as SearchIcon,
    ArrowDownward as ArrowDownwardIcon,
    AccountCircle as AccountCircleIcon,
    DeleteOutline as DeleteOutlineIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { eventService } from '../service/eventService';
import dayjs from 'dayjs';
import SuccessNotification from '../components/SuccessNotification';

export default function EventJoinees() {
    const theme = useTheme();
    const [eventGroups, setEventGroups] = useState([]);
    const [filteredGroups, setFilteredGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [refreshing, setRefreshing] = useState(false);

    // Action State
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchAllData = useCallback(async (isRefresh = false) => {
        try {
            if (!isRefresh) setLoading(true);

            // 1. Fetch all events with minimum 1s delay
            const [eventsResponse] = await Promise.all([
                eventService.getEvents(),
                new Promise(resolve => setTimeout(resolve, 1000))
            ]);

            if (eventsResponse.success && eventsResponse.data?.data) {
                const events = eventsResponse.data.data;
                const groups = [];

                // 2. For each event, fetch joined users concurrently
                await Promise.all(events.map(async (event) => {
                    try {
                        const joinResponse = await eventService.getEventJoinById(event.id);
                        if (joinResponse.success && joinResponse.data) {
                            const users = Array.isArray(joinResponse.data) ? joinResponse.data : (joinResponse.data.data || []);
                            if (users.length > 0) {
                                groups.push({ event, users });
                            }
                        }
                    } catch (err) {
                        console.error(`Failed to fetch joins for event ${event.id}`, err);
                    }
                }));

                groups.sort((a, b) => dayjs(b.event.eventDate).diff(dayjs(a.event.eventDate)));
                setEventGroups(groups);
                setFilteredGroups(groups);
            }
        } catch (error) {
            console.error('Error fetching event data:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
            if (isRefresh) {
                setNotification({ open: true, message: 'Data refreshed successfully', severity: 'success' });
            }
        }
    }, []);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    const handleSearch = (e) => {
        const term = e.target.value.toLowerCase();
        setSearchTerm(term);

        if (!term) {
            setFilteredGroups(eventGroups);
            return;
        }

        const filtered = eventGroups.filter(group => {
            const eventMatch = group.event.title.toLowerCase().includes(term);
            const userMatch = group.users.some(user =>
                user.userName.toLowerCase().includes(term) ||
                (user.mobile && user.mobile.includes(term)) ||
                (user.phoneNumber && user.phoneNumber.includes(term))
            );
            return eventMatch || userMatch;
        });

        setFilteredGroups(filtered);
    };

    const handleCall = (number) => {
        if (number) window.location.href = `tel:${number}`;
    };

    const handleDeleteClick = (id) => {
        setItemToDelete(id);
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!itemToDelete) return;
        setIsDeleting(true);
        try {
            const response = await eventService.deleteEventJoin(itemToDelete);
            if (response.success) {
                setNotification({ open: true, message: 'Joinee record removed successfully', severity: 'success' });
                fetchAllData(false); // Refresh list
                setDeleteModalOpen(false);
            }
        } catch (error) {
            console.error("Delete error", error);
        } finally {
            setIsDeleting(false);
            setItemToDelete(null);
        }
    };

    return (
        <Box sx={{ width: '100%', pb: 5 }}>
            {/* Header Area */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: { xs: 3, sm: 4 },
                p: { xs: 2, sm: 3 },
                borderRadius: 4,
                bgcolor: 'white',
                boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
                gap: 2
            }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b', letterSpacing: '-0.5px', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>Event Joinees</Typography>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                    onClick={() => { setRefreshing(true); fetchAllData(true); }}
                    sx={{
                        borderRadius: 2.5,
                        px: 3,
                        py: 1.2,
                        width: { xs: '100%', sm: 'auto' },
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '0.95rem'
                    }}
                >
                    Refresh
                </Button>
            </Box>

            {/* Search Filter */}
            <TextField
                fullWidth
                variant="outlined"
                placeholder="Search by event or user name..."
                value={searchTerm}
                onChange={handleSearch}
                sx={{ mb: 3 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                    ),
                    sx: { borderRadius: 3, bgcolor: 'background.paper' }
                }}
            />

            {loading && !refreshing ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {[1, 2, 3, 4].map((i) => (
                        <Paper key={i} sx={{ borderRadius: 4, p: 2, border: '1px solid', borderColor: 'divider' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: 3 }} />
                                <Box sx={{ flex: 1 }}>
                                    <Skeleton width="40%" height={24} sx={{ mb: 1 }} />
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Skeleton width="60px" height={16} />
                                        <Skeleton width="100px" height={16} />
                                    </Box>
                                </Box>
                                <Skeleton variant="circular" width={24} height={24} />
                            </Box>
                        </Paper>
                    ))}
                </Box>
            ) : filteredGroups.length === 0 ? (
                <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 4, bgcolor: 'background.paper' }}>
                    <AccountCircleIcon sx={{ fontSize: 64, color: 'text.disabled', opacity: 0.5, mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">No event participants found.</Typography>
                    <Typography variant="body2" color="text.disabled">Try adjusting your search or check back later.</Typography>
                </Paper>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {filteredGroups.map((group) => {
                        const { event, users } = group;
                        const usersCount = users.length;

                        return (
                            <Paper
                                key={event.id}
                                elevation={0}
                                sx={{
                                    borderRadius: 4,
                                    overflow: 'hidden',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    '&:hover': { borderColor: 'primary.main' }
                                }}
                            >
                                <Accordion elevation={0} sx={{ bgcolor: 'transparent' }}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon color="primary" />}
                                        sx={{
                                            px: 3,
                                            py: 1,
                                            '& .MuiAccordionSummary-content': { alignItems: 'center' }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                                            {/* Left Icon Strip */}
                                            <Box sx={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: 3,
                                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}>
                                                <CalendarMonthIcon color="primary" />
                                            </Box>

                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                                                    {event.title}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                                                    <Chip
                                                        label={`${usersCount} ${usersCount === 1 ? 'Joinee' : 'Joinees'}`}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                            color: theme.palette.primary.main,
                                                            fontWeight: 700,
                                                            fontSize: '0.65rem',
                                                            height: 20
                                                        }}
                                                    />
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                                                        <CalendarMonthIcon sx={{ fontSize: 14 }} />
                                                        <Typography variant="caption" fontWeight={600}>
                                                            {dayjs(event.eventDate).format('MMM D')}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </AccordionSummary>
                                    <Divider />
                                    <AccordionDetails sx={{ p: 0, bgcolor: alpha(theme.palette.primary.main, 0.01) }}>
                                        <List sx={{ p: 0 }}>
                                            {users.map((user, index) => {
                                                const mobile = user.mobile || user.phoneNumber;
                                                return (
                                                    <React.Fragment key={user.id || index}>
                                                        <ListItem
                                                            sx={{
                                                                px: 3,
                                                                py: 2,
                                                                flexDirection: { xs: 'column', sm: 'row' },
                                                                alignItems: { xs: 'flex-start', sm: 'center' },
                                                                gap: { xs: 1.5, sm: 0 },
                                                                transition: 'background 0.2s',
                                                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) }
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', flex: 1 }}>
                                                                <ListItemAvatar>
                                                                    <Avatar sx={{
                                                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                                        color: 'primary.main',
                                                                        fontWeight: 'bold',
                                                                        border: `1px solid ${theme.palette.primary.main}`
                                                                    }}>
                                                                        {user.userName ? user.userName.charAt(0).toUpperCase() : '?'}
                                                                    </Avatar>
                                                                </ListItemAvatar>
                                                                <ListItemText
                                                                    primary={
                                                                        <Typography variant="body2" fontWeight={700}>
                                                                            {user.userName}
                                                                        </Typography>
                                                                    }
                                                                    secondary={
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
                                                                            <PhoneIcon sx={{ fontSize: 12, color: 'secondary.main' }} />
                                                                            <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                                                                {mobile || 'No contact'}
                                                                            </Typography>
                                                                        </Box>
                                                                    }
                                                                />
                                                            </Box>

                                                            <Box sx={{
                                                                display: 'flex',
                                                                flexDirection: { xs: 'row', sm: 'column' },
                                                                alignItems: { xs: 'center', sm: 'flex-end' },
                                                                justifyContent: 'space-between',
                                                                width: { xs: '100%', sm: 'auto' },
                                                                pl: { xs: 7, sm: 0 }, // Align with text on mobile
                                                                gap: 1
                                                            }}>
                                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                                    <IconButton
                                                                        onClick={() => handleCall(mobile)}
                                                                        sx={{
                                                                            color: 'secondary.main',
                                                                            bgcolor: alpha(theme.palette.secondary.main, 0.05),
                                                                            '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.1) }
                                                                        }}
                                                                        size="small"
                                                                    >
                                                                        <PhoneIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        onClick={() => handleDeleteClick(user.id)}
                                                                        sx={{
                                                                            color: 'error.main',
                                                                            bgcolor: alpha(theme.palette.error.main, 0.05),
                                                                            '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) }
                                                                        }}
                                                                        size="small"
                                                                    >
                                                                        <DeleteOutlineIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6rem' }}>
                                                                    {user.joinedAt ? dayjs(user.joinedAt).format('DD/MM/YYYY') : dayjs().format('DD/MM/YYYY')}
                                                                </Typography>
                                                            </Box>
                                                        </ListItem>
                                                        {index < users.length - 1 && <Divider component="li" sx={{ ml: 9 }} />}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </List>
                                        {usersCount > 5 && (
                                            <Box sx={{ py: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, bgcolor: alpha(theme.palette.primary.main, 0.02), borderTop: '1px solid', borderColor: 'divider' }}>
                                                <ArrowDownwardIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 1, color: 'text.secondary', textTransform: 'uppercase' }}>
                                                    Scroll for more
                                                </Typography>
                                            </Box>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            </Paper>
                        );
                    })}
                </Box>
            )}

            {/* Delete Modal */}
            <Dialog
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: alpha(theme.palette.error.main, 0.1), color: 'error.main', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                        <DeleteOutlineIcon sx={{ fontSize: 32 }} />
                    </Box>
                    <Typography variant="h6" fontWeight={700} gutterBottom>Remove Joinee?</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                        Are you sure you want to remove this user from the event records?
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button fullWidth variant="outlined" onClick={() => setDeleteModalOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
                        <Button fullWidth variant="contained" color="error" onClick={handleDeleteConfirm} disabled={isDeleting} sx={{ borderRadius: 2 }}>
                            {isDeleting ? <CircularProgress size={20} color="inherit" /> : 'Remove'}
                        </Button>
                    </Box>
                </Box>
            </Dialog>

            <SuccessNotification
                open={notification.open}
                message={notification.message}
                severity={notification.severity}
                onClose={() => setNotification({ ...notification, open: false })}
            />
        </Box>
    );
}
